import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Force webpack recompile - backend pagination implemented
// import Navbar from "./NavBar";
import CustomNavbar from "./CustomPermissions/CustomNavbar";
import FilterMarketingValue from "./MarketingDateView";
import { useLocation } from "react-router-dom";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../config/api";

const MarketingFilesPage = () => {
  const [files, setFiles] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [contactPersonTypes, setContactPersonTypes] = useState([]);
  const location = useLocation();

  const stateValue = location.state && location.state["is_superuser"];

  const [isSuperuser, setIsSuperUser] = useState(false);
  const [CustomPermissions, setCustomPermissions] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [fileDetails, setFileDetails] = useState({
    name: "",
    city: "",
    state: "",
    cat: "",
    source: "",
    contact_person_type: "",
    poc_name: "",
    poc_number: "",
    date: "",
    percentage_closed: "",
    payment_in_days: "",
    scanned_file: null,
    active_status: true,
    contact_person2_type: "",
    contact_person_name2: "",
    contact_person_number2: "",
    comment: "",
    callback: "",
    callback_date: "",
    status: "Pipeline", // New status field: Onboard, Pipeline, Rejected
    onboarded_by: "", // New field: Onboarded by
    revenue: "", // New field: Revenue after onboarding
    onboarding_date: "", // New field: Date of onboarding
  });
  const [editFileId, setEditFileId] = useState(null);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});
  const recordsPerPage = 50;
  // const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchAllFiles(currentPage);
  }, []);

  useEffect(() => {
    if (currentPage > 1) {
      fetchAllFiles(currentPage, currentFilters);
    }
  }, [currentPage]);

  const fetchAllFiles = async (page = 1, filters = {}) => {
    setLoading(true);
    try {
      // Build query string with pagination and filters
      const params = new URLSearchParams({
        page: page,
        page_size: recordsPerPage,
        ...filters
      });

      const response = await axios.get(
        getApiUrl(`/Marketing/marketing-files/?${params.toString()}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      setFiles(response.data.data);
      setFilteredFiles(response.data.data);
      setTotalRecords(response.data.count);
      setTotalPages(response.data.total_pages);

      const storedPermissions =
        JSON.parse(localStorage.getItem("is_superuser")) || false;
      setIsSuperUser(storedPermissions);

      const customPerms = JSON.parse(
        localStorage.getItem("user_defined_permissions")
      );
      setCustomPermissions(customPerms);
    } catch (error) {
      console.error("Error fetching files:", error);
      toast.error("Failed to fetch marketing files");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (id) => {
    try {
      await axios.delete(
        getApiUrl(`/Marketing/marketing-files/${id}/`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      await fetchAllFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "scanned_file") {
      setFileDetails((prev) => ({ ...prev, scanned_file: files[0] }));
    } else if (name === "active_status") {
      setFileDetails((prev) => ({ ...prev, [name]: value === "true" }));
    } else {
      setFileDetails((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    Object.entries(fileDetails).forEach(([key, value]) => {
      if (['callback_date', 'onboarding_date'].includes(key) && !value) {
        return;
      }

      if (value instanceof File) {
        formData.append(key, value); // Append file objects directly
      } else if (key === "scanned_file" && !value) {
        // Skip scanned_file if it's null/empty
        return;
      } else if (key === "revenue") {
        // Handle revenue field specially - convert empty to null, otherwise ensure it's a valid decimal
        if (value === null || value === undefined || value === "" || value === "0") {
          // Don't append empty revenue - let backend handle as null
          formData.append(key, "");
        } else {
          // Ensure revenue is a valid decimal number
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            formData.append(key, numValue.toString());
          } else {
            formData.append(key, "");
          }
        }
      } else {
        // Convert all other values to string, including empty strings and null
        const stringValue = value === null || value === undefined ? "" : value.toString();
        formData.append(key, stringValue);
      }
    });

    const url = editFileId
      ? getApiUrl(`/Marketing/marketing-files/${editFileId}/`)
      : getApiUrl("/Marketing/marketing-files/");
    const method = editFileId ? "put" : "post";

    try {
      // Debug: Log form data before sending
      console.log("Form data being sent:", {
        revenue: fileDetails.revenue,
        onboarded_by: fileDetails.onboarded_by,
        editFileId: editFileId
      });

      const response = await axios({
        method,
        url,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (editFileId) {
        fetchAllFiles(currentPage, currentFilters);
      } else {
        fetchAllFiles(1, currentFilters);
      }

      setModalOpen(false);
      resetForm();
      toast.success(editFileId ? "File updated successfully" : "File created successfully");
    } catch (error) {
      console.error("Error saving file:", error);
      console.error("Error response:", error.response?.data);

      if (!error.response) {
        toast.error("Network error: " + error.message);
        return;
      }
      const data = error.response.data;
      if (!data) {
        toast.error(`Error ${error.response.status}: ${error.response.statusText}`);
        return;
      }

      if (typeof data === 'string') {
        toast.error(data.length > 100 ? "Server error occurred" : data);
      } else if (typeof data === 'object') {
        if (data.detail) {
          toast.error(data.detail);
        } else if (data.error) {
          toast.error(data.error);
        } else {
          // Handle revenue error specifically
          if (data.revenue) {
            const revMsg = Array.isArray(data.revenue) ? data.revenue.join(', ') : data.revenue;
            toast.error(`Revenue error: ${revMsg}`);
          }

          Object.keys(data).forEach((field) => {
            if (field === 'revenue') return; // Handled above
            const message = data[field];
            if (Array.isArray(message)) {
              message.forEach((msg) => toast.error(`${field}: ${msg}`));
            } else if (typeof message === 'string') {
              toast.error(`${field}: ${message}`);
            } else {
              toast.error(`${field}: Invalid value`);
            }
          });
        }
      } else {
        toast.error("An unexpected error occurred.");
      }
    }
  };

  const resetForm = () => {
    setFileDetails({
      name: "",
      city: "",
      state: "",
      cat: "",
      source: "",
      contact_person_type: "",
      poc_name: "",
      poc_number: "",
      date: "",
      percentage_closed: "",
      payment_in_days: "",
      scanned_file: null,
      active_status: true,
      contact_person2_type: "",
      contact_person_name2: "",
      contact_person_number2: "",
      comment: "",
      callback: "",
      callback_date: "",
      status: "Pipeline",
      onboarded_by: "",
      revenue: "",
      onboarding_date: "",
    });
    setEditFileId(null);
  };

  const handleEditFile = (file) => {
    setFileDetails({
      ...file,
      cat:
        categories.find((category) => category.name === file.cat__name)?.id || "",
      source:
        sources.find((source) => source.name === file.source__name)?.id || "",
      contact_person_type:
        contactPersonTypes.find(
          (type) => type.name === file.contact_person_type__name
        )?.id || "",
      contact_person2_type:
        contactPersonTypes.find(
          (type) => type.name === file.contact_person2_type__name
        )?.id || "",
      active_status: file.active_status,
      status: file.status || "Pipeline", // Default to Pipeline if status not set
      onboarded_by: file.onboarded_by || "", // Ensure onboarded_by is set
      revenue: file.revenue ? file.revenue.toString() : "", // Convert revenue to string for input
      onboarding_date: file.onboarding_date || "", // Ensure onboarding_date is set
      // Handle uploaded_file correctly
      scanned_file: file.scanned_file
        ? typeof file.scanned_file === "string"
          ? null // Avoid creating a File object if it's a string
          : new File([file.scanned_file], file.scanned_file.name)
        : null,
    });
    setEditFileId(file.id);
    setModalOpen(true);
  };

  // useEffect(() => {
  //   handleFilteredResults(filteredFiles);
  // }, [filteredFiles])

  const handleFilteredResults = (filters) => {
    // Send filters to backend API which will search across ALL records
    // Build filter object for API
    const apiFilters = {};

    if (filters.query) apiFilters.query = filters.query;
    if (filters.location) apiFilters.location = filters.location;
    if (filters.state) apiFilters.state = filters.state;
    if (filters.category) apiFilters.category = filters.category;
    if (filters.percentageClosed) apiFilters.percentageClosed = filters.percentageClosed;
    if (filters.selectedMonth) apiFilters.selectedMonth = filters.selectedMonth;
    if (filters.selectedYear) apiFilters.selectedYear = filters.selectedYear;
    if (filters.activeStatus) apiFilters.activeStatus = filters.activeStatus;
    if (filters.source) apiFilters.source = filters.source;
    if (filters.onboardedBy) apiFilters.onboardedBy = filters.onboardedBy;

    // Store current filters for pagination
    setCurrentFilters(apiFilters);

    // Reset to page 1 and fetch with filters
    setCurrentPage(1);
    fetchAllFiles(1, apiFilters);
  };

  const toggleRowExpansion = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const onClose = () => {
    setModalOpen(false);
    setFileDetails({
      name: "",
      city: "",
      state: "",
      cat: "",
      source: "",
      contact_person_type: "",
      poc_name: "",
      poc_number: "",
      date: "",
      percentage_closed: "",
      payment_in_days: "",
      scanned_file: null,
      contact_person2_type: "",
      contact_person_name2: "",
      contact_person_number2: "",
      comment: "",
      callback: "",
      callback_date: "",
      status: "Pipeline",
      onboarded_by: "",
      revenue: "",
      onboarding_date: "",
    });
    setEditFileId(null);
  };

  useEffect(() => {
    const config = {
      headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
    };

    axios
      .get(getApiUrl("/Marketing/vacancies/"), config)
      .then((response) => setVacancies(response.data))
      .catch((error) =>
        console.error("There was an error fetching the vacancies!", error)
      );

    axios
      .get(getApiUrl("/Marketing/categories/"), config)
      .then((response) => setCategories(response.data))
      .catch((error) =>
        console.error("There was an error fetching the categories!", error)
      );

    axios
      .get(getApiUrl("/Marketing/marketing-sources/"), config)
      .then((response) => setSources(response.data))
      .catch((error) =>
        console.error("There was an error fetching the sources!", error)
      );

    axios
      .get(getApiUrl("/Marketing/contact-person-types/"), config)
      .then((response) => setContactPersonTypes(response.data))
      .catch((error) =>
        console.error(
          "There was an error fetching the contact person types!",
          error
        )
      );
  }, []);

  const totalClients = files.length;
  const activeClientsCount = files.filter((file) => file.active_status).length;
  const inactiveClientsCount = totalClients - activeClientsCount;

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden"; // Prevent background scrolling
      document.getElementById("marketingmodalopen").overflow = "scroll";
    } else {
      document.body.style.overflow = "auto"; // Restore scrolling
    }

    return () => {
      document.body.style.overflow = "auto"; // Cleanup when modal closes
    };
  }, [modalOpen]);

  return (
    <div className="font-sans" style={{ display: "flex" }}>
      <CustomNavbar />

      {/* <div style={{ minWidth: "90vw", margin: "auto" }}> */}
      <div>
        <div
          className="container mt-3 mb-2"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <h1 className="text-center text-5xl text-blue-700 mb-2">
            Marketing Files
          </h1>
          <button
            className="bg-blue-500 text-white px-4 py-2 mb-2 rounded hover:bg-blue-900 transition-colors"
            onClick={() => {
              setModalOpen(true);
              setEditFileId(null);
            }}
          >
            Add New File
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3 container">
          <div className="p-2 bg-blue-500 rounded shadow text-center">
            <h4 className="text-lg font-semibold text-white">Total Clients</h4>
            <p className="text-xl font-bold text-white">{totalClients}</p>
          </div>
          <div className="p-2 bg-green-500 rounded shadow text-center">
            <h4 className="text-lg font-semibold text-white">Active Clients</h4>
            <p className="text-xl font-bold text-white">{activeClientsCount}</p>
          </div>
          <div className="p-2 bg-red-500 rounded shadow text-center">
            <h4 className="text-lg font-semibold text-white">
              Inactive Clients
            </h4>
            <p className="text-xl font-bold text-white">
              {inactiveClientsCount}
            </p>
          </div>
        </div>

        <div className="">
          <div className="mt-2 mb-5">
            {/* <FilterMarketingValue onResults={handleFilteredResults} /> */}
            <div className="flex justify-between items-center mb-3 px-4">
              <div className="flex-1">
                <FilterMarketingValue onResults={handleFilteredResults} />
              </div>
            </div>
            {/* <h2 className="text-center text-2xl text-blue-700 mb-2">
              All Files
            </h2> */}
            <div className="overflow-x-auto mx-auto w-full">
              <table
                className="text-sm border-separate border-spacing-y-2 bg-white border-gray-300 overflow-x-auto"
                style={{ width: "95%", margin: "auto", minWidth: "95%", tableLayout: "fixed" }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#3F83F8" }}>
                    <th className="py-2 px-4 border-b" style={{ width: "4%" }}>Sr. No</th>
                    <th className="py-2 px-4 border-b" style={{ width: "23%" }}>Client Name</th>
                    <th className="py-2 px-4 border-b" style={{ width: "9%" }}>City</th>
                    <th className="py-2 px-4 border-b" style={{ width: "9%" }}>State</th>
                    <th className="py-2 px-4 border-b" style={{ width: "10%" }}>Date</th>
                    <th className="py-2 px-4 border-b" style={{ width: "3%" }}>Category</th>
                    <th className="py-2 px-4 border-b" style={{ width: "10%", paddingLeft: "25px" }}>Active Status</th>
                    <th className="py-2 px-4 border-b" style={{ width: "11%" }}>Source</th>
                    <th className="py-2 px-4 border-b" style={{ width: "7%" }}>%</th>
                    <th className="py-2 px-4 border-b" style={{ width: "14%" }}>Payment Terms (Days)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        Loading...
                      </td>
                    </tr>
                  ) : filteredFiles.length > 0 ? (
                    filteredFiles.map((file, index) => (
                      <React.Fragment key={file.id}>
                        <tr
                          className="cursor-pointer bg-white hover:bg-gray-100 transition-colors"
                          style={{ fontSize: "14px" }}
                          onClick={() => toggleRowExpansion(index)}
                        >
                          <td className="py-2 px-4 border-b text-center">
                            {(currentPage - 1) * recordsPerPage + index + 1}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {file.name}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {file.city}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {file.state}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {file.date
                              ? new Date(file.date).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })
                              : '-'}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {file.cat__name}
                          </td>
                          <td className="py-2 px-4 border-b text-center" style={{ paddingLeft: "25px" }}>
                            {file.active_status ? "Active" : "Inactive"}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {file.source__name}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {file.percentage_closed}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {file.payment_in_days}
                          </td>
                        </tr>
                        {expandedRow === index && (
                          <tr className="bg-gray-50">
                            <td colSpan="10" className="px-4 py-6">
                              {/* Contact Information */}
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                                <p>
                                  <strong>Contact Person Type:</strong>{" "}
                                  {file.contact_person_type__name}
                                </p>
                                <p>
                                  <strong>Name:</strong> {file.poc_name}
                                </p>
                                <p>
                                  <strong>Number:</strong> {file.poc_number}
                                </p>

                                <p>
                                  <strong>Contact Person 2 Type:</strong>{" "}
                                  {file.contact_person2_type__name}
                                </p>
                                <p>
                                  <strong>Name:</strong>{" "}
                                  {file.contact_person_name2}
                                </p>
                                <p>
                                  <strong>Number:</strong>{" "}
                                  {file.contact_person_number2}
                                </p>

                                <p>
                                  <strong>Callback:</strong> {file.callback}
                                </p>
                                <p>
                                  <strong>Callback Date:</strong>{" "}
                                  {file.callback_date}
                                </p>
                                <p>
                                  <strong>Status:</strong> {file.status || "Pipeline"}
                                </p>
                                <p>
                                  <strong>Onboarded By:</strong> {file.onboarded_by || "N/A"}
                                </p>
                                <p>
                                  <strong>Revenue:</strong> {file.revenue ? `₹${file.revenue}L` : "N/A"}
                                </p>
                                <p>
                                  <strong>Onboarding Date:</strong> {file.onboarding_date ? new Date(file.onboarding_date).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  }) : "N/A"}
                                </p>
                              </div>

                              {/* Comment Section */}
                              <p className="mb-4">
                                <strong>Comment:</strong> {file.comment}
                              </p>

                              {/* Action Buttons */}
                              <div className="flex justify-center gap-3">
                                {/* View Button */}
                                {/* {CustomPermissions &&
                                  CustomPermissions.includes(
                                    "View Marketing File"
                                  ) && (
                                    <>
                                      <a
                                        href={getApiUrl(`/media/${file.scanned_file}/`)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          className="bi bi-eye"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                                          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                                        </svg>
                                      </a>
                                    </>
                                  )} */}

                                {CustomPermissions &&
                                  CustomPermissions.includes("View Marketing File") &&
                                  file.scanned_file && (
                                    <a
                                      href={getApiUrl(`/media/${file.scanned_file}/`)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-500 hover:text-blue-800 flex items-center gap-1"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        className="bi bi-eye"
                                        viewBox="0 0 16 16"
                                      >
                                        <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z" />
                                        <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
                                      </svg>
                                    </a>
                                  )}
                                {CustomPermissions &&
                                  CustomPermissions.includes("View Marketing File") &&
                                  !file.scanned_file && (
                                    <span className="text-gray-500">No file available</span>
                                  )}

                                {/* Edit Button */}
                                {CustomPermissions &&
                                  CustomPermissions.includes(
                                    "Edit Marketing"
                                  ) && (
                                    <>
                                      <button
                                        onClick={() => handleEditFile(file)}
                                        className="text-green-600 hover:text-green-800 flex items-center gap-1"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          className="bi bi-pen-fill"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="m13.498.795.149-.149a1.207 1.207 0 1 1 1.707 1.708l-.149.148a1.5 1.5 0 0 1-.059 2.059L4.854 14.854a.5.5 0 0 1-.233.131l-4 1a.5.5 0 0 1-.606-.606l1-4a.5.5 0 0 1 .131-.232l9.642-9.642a.5.5 0 0 0-.642.056L6.854 4.854a.5.5 0 1 1-.708-.708L9.44.854A1.5 1.5 0 0 1 11.5.796a1.5 1.5 0 0 1 1.998-.001" />
                                        </svg>
                                      </button>
                                    </>
                                  )}

                                {/* Delete Button */}

                                {isSuperuser ? (
                                  <>
                                    <button
                                      onClick={() => {
                                        const confirmDelete = window.confirm(
                                          "Are you sure you want to delete this file? This action cannot be undone."
                                        );
                                        if (confirmDelete) {
                                          handleDeleteFile(file.id);
                                        }
                                      }}
                                      className="text-red-600 hover:text-red-800 flex items-center gap-1"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        className="bi bi-trash-fill"
                                        viewBox="0 0 16 16"
                                      >
                                        <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0" />
                                      </svg>
                                    </button>
                                    {/* <button
                                variant="outlined"
                                sx={{
                                  color: "#dc2626",
                                  borderColor: "#dc2626",
                                  "&:hover": { borderColor: "#ef4444" },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFile(file.id);
                                }}
                              >
                                Delete
                              </button> */}
                                  </>
                                ) : (
                                  <></>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        No records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {totalRecords > 0 && (
                <div className="flex justify-between items-center mt-4 px-4">
                  <div className="text-sm text-gray-600">
                    Showing {Math.min((currentPage - 1) * recordsPerPage + 1, totalRecords)} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} records
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const newPage = Math.max(currentPage - 1, 1);
                        setCurrentPage(newPage);
                        fetchAllFiles(newPage, currentFilters);
                      }}
                      disabled={currentPage === 1 || loading}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {(() => {
                      const pageNumbers = [];
                      const maxPagesToShow = 5;

                      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

                      if (endPage - startPage < maxPagesToShow - 1) {
                        startPage = Math.max(1, endPage - maxPagesToShow + 1);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pageNumbers.push(
                          <button
                            key={i}
                            onClick={() => {
                              setCurrentPage(i);
                              fetchAllFiles(i, currentFilters);
                            }}
                            disabled={loading}
                            className={`px-3 py-1 rounded ${currentPage === i
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              } ${loading ? 'cursor-not-allowed' : ''}`}
                          >
                            {i}
                          </button>
                        );
                      }

                      return (
                        <>
                          {startPage > 1 && (
                            <>
                              <button
                                onClick={() => {
                                  setCurrentPage(1);
                                  fetchAllFiles(1, currentFilters);
                                }}
                                disabled={loading}
                                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                1
                              </button>
                              {startPage > 2 && <span className="px-2">...</span>}
                            </>
                          )}
                          {pageNumbers}
                          {endPage < totalPages && (
                            <>
                              {endPage < totalPages - 1 && <span className="px-2">...</span>}
                              <button
                                onClick={() => {
                                  setCurrentPage(totalPages);
                                  fetchAllFiles(totalPages, currentFilters);
                                }}
                                disabled={loading}
                                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                {totalPages}
                              </button>
                            </>
                          )}
                        </>
                      );
                    })()}

                    <button
                      onClick={() => {
                        const newPage = Math.min(currentPage + 1, totalPages);
                        setCurrentPage(newPage);
                        fetchAllFiles(newPage, currentFilters);
                      }}
                      disabled={currentPage === totalPages || loading}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {modalOpen && (
          <div
            className="fixed inset-0 flex justify-center items-center z-50"
            id="marketingmodalopen"
            style={{ width: "100vw", height: "100vh" }}
          >
            {/* <div className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm"></div> */}
            <div
              className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm"
              style={{
                width: "100vw",
                height: "150%",
                background: "black",
                backgroundSize: "200% 200vh",
                overflowY: "hidden",
                paddingBottom: "150px",
              }}
            ></div>
            {/* <div className="relative bg-white w-4/5 max-w-4xl rounded-lg shadow-lg" style={{ marginTop: "100px" }}> */}
            <div
              className="relative bg-white w-4/5 max-w-4xl rounded-lg shadow-lg"
              style={{
                // marginTop: "50px",
                height: "95vh", // Set height to allow scrolling
                overflowY: "auto", // Enable scrolling inside modal
              }}
            >
              <div
                className="overflow-auto max-h-[80vh]"
                style={{ padding: "100px", paddingTop: "20px" }}
              >
                <h2 className="text-center text-blue-700 mb-5">Add File</h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-red-700 transition-colors"
                  style={{ position: "absolute", top: "20px", right: "20px" }}
                >
                  X
                </button>
                <form
                  onSubmit={handleSubmit}
                  className="grid grid-cols-3 sm:grid-cols-1 gap-4"
                >
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={fileDetails.name}
                    onChange={handleFormChange}
                    required
                    className="w-full p-2 border rounded input-field"
                  />
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={fileDetails.city}
                    onChange={handleFormChange}
                    required
                    className="w-full p-2 border rounded input-field"
                  />

                  <input
                    type="text"
                    name="state"
                    placeholder="State"
                    value={fileDetails.state}
                    onChange={handleFormChange}
                    required
                    className="w-full p-2 border rounded input-field"
                  />
                  <input
                    type="date"
                    name="date"
                    value={fileDetails.date}
                    onChange={handleFormChange}
                    required
                    className="w-full p-2 border rounded input-field"
                  />

                  <select
                    name="cat"
                    value={fileDetails.cat}
                    onChange={handleFormChange}
                    required
                    className="col-span-1 p-2 border rounded input-field"
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>

                  <select
                    name="active_status"
                    value={fileDetails.active_status}
                    onChange={handleFormChange}
                    required
                    className="col-span-1 p-2 border rounded input-field"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>

                  <select
                    name="source"
                    value={fileDetails.source}
                    onChange={handleFormChange}
                    required
                    className="col-span-1 p-2 border rounded input-field"
                  >
                    <option value="">Select Source</option>
                    {sources.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.name}
                      </option>
                    ))}
                  </select>

                  <select
                    name="contact_person_type"
                    value={fileDetails.contact_person_type}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  >
                    <option value="">Select Contact Person Type</option>
                    {contactPersonTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    name="poc_name"
                    placeholder="POC Name"
                    value={fileDetails.poc_name}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <input
                    type="text"
                    name="poc_number"
                    placeholder="POC Number"
                    value={fileDetails.poc_number}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />

                  <select
                    name="contact_person2_type"
                    value={fileDetails.contact_person2_type}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  >
                    <option value="">Select Contact Person Type</option>
                    {contactPersonTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    name="contact_person_name2"
                    placeholder="Contact Person Name"
                    value={fileDetails.contact_person_name2}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <input
                    type="text"
                    name="contact_person_number2"
                    placeholder="Contact Person Number"
                    value={fileDetails.contact_person_number2}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />

                  <input
                    type="text"
                    name="percentage_closed"
                    placeholder="Percentage Closed"
                    value={fileDetails.percentage_closed}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <input
                    type="text"
                    name="payment_in_days"
                    placeholder="Payment in Days"
                    value={fileDetails.payment_in_days}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <input
                    type="text"
                    name="callback"
                    placeholder="Callback"
                    value={fileDetails.callback}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <input
                    type="date"
                    name="callback_date"
                    placeholder="Callback Date"
                    value={fileDetails.callback_date}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <textarea
                    name="comment"
                    placeholder="Remarks"
                    value={fileDetails.comment}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <select
                    name="status"
                    value={fileDetails.status || "Pipeline"}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                    required
                  >
                    <option value="Pipeline">Pipeline</option>
                    <option value="Onboard">Onboard</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                  <input
                    type="text"
                    name="onboarded_by"
                    placeholder="Onboarded by"
                    value={fileDetails.onboarded_by || ""}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <input
                    type="number"
                    name="revenue"
                    placeholder="Revenue after onboarding"
                    value={fileDetails.revenue || ""}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                    step="0.01"
                  />
                  <input
                    type="date"
                    name="onboarding_date"
                    placeholder="Onboarding Date"
                    value={fileDetails.onboarding_date || ""}
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <input
                    type="file"
                    name="scanned_file"
                    onChange={handleFormChange}
                    className="col-span-1 p-2 border rounded input-field"
                  />
                  <br />

                  <div className="flex justify-between items-center col-span-3">
                    <button
                      type="submit"
                      className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-500 transition-colors"
                    >
                      {editFileId ? "Update" : "Create"}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default MarketingFilesPage;
